// src/interactionLogic.js
import { API_BASE_URL } from './pages/Character'

class InteractionManager {
    constructor() {
        // Data loaded from backend
        this.videoGraph = null;
        this.availableActions = null;
        this.characterConfig = null;

        // Session state
        this.currentNode = 1;
        this.chatHistory = [];
        this.gameChatHistory = [];
        this.playMode = false;
        this.currentScore = 0;
        this.exclusive_actions = [
            {
                "id": 1,
                "title": "slow dance and touch smooth",
                "lock": true,
                "points": 10,
                "starting_node": 6,
                "ending_node": 6,
                "seq": ["6-6 slow dance and touch smooth.mp4"]
            }
        ]
    }

    async initialize(character) {
        try {
            const [responses] = await Promise.all([
                fetch(`${API_BASE_URL}/api/character-config?character=${character}`),
            ]);
            let configRes = await responses.json();
            if (configRes.error){
                throw new Error(configRes.error);
            }
            this.characterConfig = configRes.character;
            this.videoGraph = configRes.graph;
            this.availableActions = configRes.actions;

            console.log("InteractionManager Initialized", this);
            return true;
        } catch (error) {
            console.error("Failed to initialize InteractionManager:", error);
            return false;
        }
    }

    findCategoryVideo(categoryKeyword, node = null) {
        const targetNode = node ?? this.currentNode;
        const videos = this.videoGraph[targetNode]?.[targetNode] || [];

        const matches = videos.filter(v => v.category.includes(categoryKeyword));
        console.log(`[Category]: ${categoryKeyword}, Matches: ${matches.length}`);

        if (matches.length > 0) {
            return matches[Math.floor(Math.random() * matches.length)];
        }

        // Fallback to any video in the node if no category match
        if (videos.length > 0) {
            return videos[Math.floor(Math.random() * videos.length)];
        }

        return null;
    }

    findVideoByFilename(filename, node = null) {
        const targetNode = node ?? this.currentNode;
        const videos = this.videoGraph[targetNode]?.[targetNode] || [];

        const matches = videos.filter(v => v.filename.includes(filename));
        console.log(`Matches: ${matches.length}`);

        if (matches.length > 0) {
            return matches[0];
        }

        return null;
    }

    findTransitionVideo(startNode, nextNode) {
        console.log(`Transition from node ${startNode} to node ${nextNode}`)
        const transitionsVidos = this.videoGraph[startNode][nextNode] || [];

        if (transitionsVidos.length > 0) {
            return transitionsVidos[Math.floor(Math.random() * transitionsVidos.length)];
        }

        return null;
    }

    getInitialVideo() {
        return this.findCategoryVideo(this.characterConfig.defaults.greeting);
    }

    // This is the main function the UI will call
    async processUserChatQuery(userText) {
        const actionQueue = [];

        // // 1. IMMEDIATE: Queue "Typing" video
        // const typingVid = this.findCategoryVideo(this.characterConfig.defaults.typing);
        // if (typingVid) {
        //     actionQueue.push({ type: 'video', data: typingVid });
        // }

        // 2. PROCESS: Call LLM via our secure backend proxy
        this.chatHistory.push({ role: "user", content: userText });
        const recentHistory = this.chatHistory.slice(-5);

        let systemPrompt = this.characterConfig.system_prompt.replace(
            "[AVAILABLE_ACTIONS]",
            JSON.stringify(this.availableActions[this.currentNode][this.currentNode] || [])
        );
        systemPrompt += `\n\n**Previous Conversation Context:**\n${JSON.stringify(recentHistory)}`;

        const payload_messages = [{ role: "system", content: systemPrompt }];

        const llmResponseText = await this._callLLMProxy(payload_messages);

        // --- Parse LLM Response ---
        let nextAction = "listen"; // Fallback
        let characterResponse = "Sorry, something went wrong. Please try again.";
        let needTyping = false;

        if (llmResponseText) {
            try {
                const actionMatch = llmResponseText.match(/\[REACTION\]:\s*(.*)/);
                const textMatch = llmResponseText.match(/\[TEXT\]:\s*(.*)/);
                const needTypingMatch = llmResponseText.match(/\[TYPING\]:\s*(.*)/);

                if (textMatch && actionMatch) {
                    nextAction = actionMatch[1].trim().toLowerCase();
                    characterResponse = textMatch[1].trim();
                } else {
                    console.warn("Could not parse LLM response format.");
                    // Use the full response if parsing fails
                    characterResponse = llmResponseText;
                }

                if (needTypingMatch && needTypingMatch == "1") {
                    needTyping = true;
                }

            } catch (e) {
                console.error("Failed to parse LLM response", e);
            }
        }
        
        // IMMEDIATE: Queue "Typing" video
        if (needTyping){
            const typingVid = this.findCategoryVideo(this.characterConfig.defaults.typing);
            if (typingVid) {
                actionQueue.push({ type: 'video', data: typingVid });
            }
        }

        // RESULT: Queue Response Text and Video
        this.chatHistory.push({ role: "assistant", content: characterResponse });
        actionQueue.push({ type: 'message', data: { text: characterResponse } });

        // IMMEDIATE: Queue "Reaction" video
        const actionVid = this.findCategoryVideo(nextAction);
        if (actionVid) {
            actionQueue.push({ type: 'video', data: actionVid });
        }


        return actionQueue;
    }

    async processUserGameQuery(userText) {
        const actionQueue = [];

        // Fallback variables
        let nextAction = "listen";
        let characterResponseObj = {
            commentary: ""
        }
        let llmResponse = null;
        let parsedData = {};
        let recentGameHistory = [];

        try {

            this.gameChatHistory.push({ role: "user", content: userText });
            recentGameHistory = this.gameChatHistory.slice(-2); // Only last 2 QnA for context
            console.log("[recentGameHistory]", recentGameHistory.length)

            const gameSystemPromptKey = 'game_system_prompt';
            if (!this.characterConfig[gameSystemPromptKey]) {
                throw new Error(`The selected character is missing the '${gameSystemPromptKey}' key.`);
            }

            let gameSystemPrompt = this.characterConfig[gameSystemPromptKey].replace(
                "[AVAILABLE_ACTIONS]",
                JSON.stringify(this.availableActions[this.currentNode][this.currentNode] || [])
            );
            gameSystemPrompt += `\n\n**PREVIOUS QnA CONTEXT**\n${JSON.stringify(recentGameHistory)}`;

            const payload_messages = [{ role: "system", content: gameSystemPrompt }];

            // Call LLM via secure backend proxy
            llmResponse = await this._callLLMProxy(payload_messages);

            // 2. PARSE LLM Response
            if (llmResponse) {
                try {
                    // Simplified regex parsing for structured data extraction
                    const extractGroup = (regex) => {
                        const match = llmResponse.match(regex);
                        return match ? match[1].trim() : null;
                    };

                    parsedData.action = extractGroup(/\[ACTION\]:\s*(.*)/);
                    parsedData.last_score = extractGroup(/\[LAST_SCORE\]:\s*(.*)/);
                    parsedData.commentary = extractGroup(/\[TEXT\]:\s*(.*)/);
                    parsedData.question = extractGroup(/\[QUESTION\]:\s*(.*)/);
                    parsedData.options = [];

                    // Extracting Options Block
                    const optionsBlockMatch = llmResponse.match(/\[OPTIONS\]:\n(.*?)\n\[END_OPTIONS\]/s);
                    if (optionsBlockMatch) {
                        const optionsBlock = optionsBlockMatch[1];
                        const lines = optionsBlock.trim().split('\n');

                        for (const line of lines) {
                            // Splits "A) Save the lab | Intellect" into parts
                            const parts = line.split('|');
                            const optionText = parts[0] ? parts[0].trim() : '';
                            const trait = parts[1] ? parts[1].trim() : '';

                            if (optionText && trait) {
                                parsedData.options.push({ text: optionText, trait: trait });
                            }
                        }
                    }

                    this.currentScore += parseInt(parsedData.last_score)
                    console.log("Score:", this.currentScore)

                    // --- Construct Response ---
                    if (parsedData.commentary && parsedData.question && parsedData.options.length > 0) {
                        nextAction = parsedData.action ? parsedData.action.trim().toLowerCase() : this.characterConfig.defaults.listening;

                        characterResponseObj = {
                            commentary: parsedData.commentary,
                            question: parsedData.question,
                            options: parsedData.options // Keep this as the original array [{text: '...'}, ...]
                        }

                    } else {
                        console.warn("LLM response missing required game fields (ACTION, TEXT, QUESTION, OPTIONS).");
                        // Fallback to basic error message
                    }

                } catch (e) {
                    console.error("Failed to parse LLM game response:", e);
                    // The characterResponse remains the default error message
                }
            }

        } catch (e) {
            console.error("Error during processUserGameQuery execution:", e);
        }

        // 3. QUEUE ACTIONS
        // Skipping Reaction video for new conversation
        if (recentGameHistory.length > 1){
            // Push reaction video (e.g., "correct", "incorrect")
            const reactionVid = this.findCategoryVideo(nextAction);
            if (reactionVid) {
                actionQueue.push({ type: 'video', data: reactionVid });
            }
        }

        // Push "Typing" video immediately after reaction, while message is being displayed
        const typingVid = this.findCategoryVideo(this.characterConfig.defaults.typing);
        if (typingVid) {
            actionQueue.push({ type: 'video', data: typingVid });
        }

        // Save full raw AI response to history for the next prompt context
        if (llmResponse) {
            this.gameChatHistory.push({ role: "assistant", content: llmResponse });
        }

        // Push the formatted message
        actionQueue.push({ type: 'qna_message', data: {...characterResponseObj} });

        return actionQueue;
    }

    processUserActionSelection(transition_id){
        const actionQueue = [];
        // Find the transition in available actions
        const transitions = this.exclusive_actions.filter((action) => action.id === transition_id)
        if (transitions.length > 0){
            const selectedTransition = transitions[0];
            const transition_starting_video = this.findTransitionVideo(this.currentNode, selectedTransition.starting_node);
            const transition_ending_node = this.findTransitionVideo(selectedTransition.ending_node, this.currentNode);
            if (transition_starting_video && transition_ending_node){

                actionQueue.push({type: 'video', data: transition_starting_video})

                selectedTransition.seq.forEach(filename => {
                    actionQueue.push({ 
                        type: 'video', 
                        data: {
                            "filename": filename,
                            "category": "transitions",
                            "duration": null
                        }
                    });
                });

                actionQueue.push({type: 'video', data: transition_ending_node}) 
            }
        }

        return actionQueue
    }

    async _callLLMProxy(messages) {
        try {
            const response = await fetch(`${API_BASE_URL}/api/llm-proxy`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ messages: messages })
            });

            if (!response.ok) {
                throw new Error(`LLM Proxy Error: ${response.statusText}`);
            }

            const data = await response.json();
            console.log("[LLM Response]:", data.response);
            return data.response;
        } catch (error) {
            console.error("Failed to call LLM Proxy:", error);
            return ""; // Return empty string on failure
        }
    }
}

export const interactionManager = new InteractionManager();